import { ChangeEvent, useEffect, useState } from "react";
import { z } from "zod";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import Button from "@material-ui/core/Button";
import AddCircle from "@material-ui/icons/AddCircle";
import Dialog from "@material-ui/core/Dialog";
import Card from "@material-ui/core/Card";
import CardHeader from "@material-ui/core/CardHeader";
import Typography from "@material-ui/core/Typography";
import CardContent from "@material-ui/core/CardContent";
import CardActions from "@material-ui/core/CardActions";
import FormHelperText from '@material-ui/core/FormHelperText';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from "@material-ui/core/FormControlLabel";
import InputMask from "react-input-mask";
import TextField from "@material-ui/core/TextField";
import { UseMutationResult } from "@tanstack/react-query";
import { schema } from "./schema";
import { useFormStyles } from "./styles";
import { useAttendanceLocalStyles } from "../../../../../../../../../styles";
import { AttendanceLocalDepartmentQueue } from "../../../../../../../../../../../@types/attendance-local-department-queue";
import { AttendanceLocalDepartmentQueueCreateData } from "../../../../../../../../../../../services/api-tansquery/attendance-local/department/queue/create";
import { AttendanceLocalDepartmentQueueUpdateData } from "../../../../../../../../../../../services/api-tansquery/attendance-local/department/queue/update";
import { AttendanceLocalDepartment } from "../../../../../../../../../../../@types/attendance-local-department";
import { CalendarToday } from "@material-ui/icons";
import { Grid, Switch } from "@material-ui/core";
import { AttendanceLocal } from "../../../../../../../../../../../@types/attendance-local";

type FormProps = {
    departmentId: AttendanceLocalDepartment["id"];
    slug: AttendanceLocal["slug_unidade"];
    queue?: AttendanceLocalDepartmentQueue;
    buttonTrigger?: React.ReactNode;
    mutation: UseMutationResult<
        AttendanceLocalDepartmentQueue,
        unknown,
        | AttendanceLocalDepartmentQueueCreateData
        | AttendanceLocalDepartmentQueueUpdateData,
        unknown
    >
};

export default function Form({ departmentId, slug, queue, buttonTrigger, mutation }: FormProps) {
    const [open, setOpen] = useState(false);
    const { classes } = useFormStyles();
    const { classes: defaultClasses } = useAttendanceLocalStyles();
    const form = useForm<z.infer<typeof schema>>({
        resolver: zodResolver(schema),
        defaultValues: {
            setor: departmentId.toString(),
            unidade: slug,
            data_inicio: "",
            data_fim: "",
            ignorar_feriados: false,
            sabado: false,
            domingo: false,
        }
    });

    async function handleSubmit(data: z.infer<typeof schema>) {
        if (mutation.isLoading) {
            return;
        }

        await mutation.mutateAsync({
            setor: Number(data.setor),
            unidade: data.unidade,
            data_inicio: data.data_inicio.split("/").reverse().join("-"),
            data_fim: data.data_fim.split("/").reverse().join("-"),
            ignorar_feriados: data.ignorar_feriados,
            sabado: data.sabado,
            domingo: data.domingo
        });
    }

    useEffect(() => {
        if (open && queue) {
            form.reset({
                setor: queue.setor.toString(),
                unidade: slug,
                data_inicio: queue.data_inicio.split("-").reverse().join("/"),
                data_fim: queue.data_fim.split("-").reverse().join("/"),
                ignorar_feriados: queue.ignorar_feriados,
                sabado: queue.sabado,
                domingo: queue.domingo,
            });
        } else {
            form.reset();
        }
    }, [open, queue, form, slug]);

    useEffect(() => {
        if (mutation.isSuccess) {
            setOpen(false);
        }
    }, [mutation.isSuccess]);

    return (
        <>
            {buttonTrigger ? (
                <span onClick={() => setOpen(true)}>
                    {buttonTrigger}
                </span>
            ) : (
                <Button
                    startIcon={<AddCircle />}
                    className={defaultClasses.addButton}
                    onClick={() => setOpen(true)}
                >
                    Fila
                </Button>
            )}
            <Dialog open={open}>
                <Card component="form" onSubmit={form.handleSubmit(handleSubmit)}>
                    <CardHeader
                        avatar={<AddCircle />}
                        className={classes.cardHeader}
                        title={<Typography variant="h5">Fila</Typography>}
                    />
                    <CardContent
                        container
                        spacing={2}
                        component={Grid}
                    >
                        {!queue?.id && (
                            <>
                                <Grid item xs={6}>
                                    <Controller
                                        name="data_inicio"
                                        control={form.control}
                                        render={({ field, fieldState }) => (
                                            <InputMask
                                                mask="99/99/9999"
                                                maskChar={null}
                                                value={field.value || ""}
                                                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                                    field.onChange(e.target.value);
                                                }}
                                            >
                                                {(inputProps: any) => (
                                                    <TextField
                                                        {...inputProps}
                                                        required
                                                        fullWidth
                                                        margin="normal"
                                                        variant="outlined"
                                                        label="Data início"
                                                        inputProps={{ className: classes.textField }}
                                                        error={!!fieldState.error}
                                                        helperText={
                                                            fieldState.error ? fieldState.error.message : ""
                                                        }
                                                        InputProps={{
                                                            required: false,
                                                            endAdornment: <CalendarToday htmlColor="#C4C4C4" />
                                                        }}
                                                    />
                                                )}
                                            </InputMask>
                                        )}
                                    />
                                </Grid>

                                <Grid item xs={6}>
                                    <Controller
                                        name="data_fim"
                                        control={form.control}
                                        render={({ field, fieldState }) => (
                                            <InputMask
                                                mask="99/99/9999"
                                                maskChar={null}
                                                value={field.value || ""}
                                                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                                    field.onChange(e.target.value);
                                                }}
                                            >
                                                {(inputProps: any) => (
                                                    <TextField
                                                        {...inputProps}
                                                        required
                                                        fullWidth
                                                        margin="normal"
                                                        variant="outlined"
                                                        label="Data fim"
                                                        inputProps={{ className: classes.textField }}
                                                        error={!!fieldState.error}
                                                        helperText={
                                                            fieldState.error ? fieldState.error.message : ""
                                                        }
                                                        InputProps={{
                                                            required: false,
                                                            endAdornment: <CalendarToday htmlColor="#C4C4C4" />
                                                        }}
                                                    />
                                                )}
                                            </InputMask>
                                        )}
                                    />
                                </Grid>
                            </>
                        )}

                        <Grid item xs={12} component={Typography}>
                            Configuração de atividade
                        </Grid>
                        <Grid item xs={12} style={{ paddingLeft: 10 }}>
                            <Controller
                                name="sabado"
                                control={form.control}
                                render={({ field }) => (
                                    <FormControl>
                                        <FormControlLabel
                                            label="Incluir Sábado"
                                            control={<Switch {...field} checked={field.value} />}
                                        />
                                        <FormHelperText>
                                            Marque esta opção para permitir esta fila estar disponível no sábado.
                                        </FormHelperText>
                                    </FormControl>
                                )}
                            />
                        </Grid>

                        <Grid item xs={12} style={{ paddingLeft: 10 }}>
                            <Controller
                                name="domingo"
                                control={form.control}
                                render={({ field }) => (
                                    <FormControl>
                                        <FormControlLabel
                                            label="Incluir Domingo"
                                            control={<Switch {...field} checked={field.value} />}
                                        />
                                        <FormHelperText>
                                            Marque esta opção para permitir esta fila estar disponível no domingo.
                                        </FormHelperText>
                                    </FormControl>
                                )}
                            />
                        </Grid>

                        <Grid item xs={12} style={{ paddingLeft: 10 }}>
                            <Controller
                                name="ignorar_feriados"
                                control={form.control}
                                render={({ field }) => (
                                    <FormControl>
                                        <FormControlLabel
                                            label="Ignorar Feriados"
                                            control={<Switch {...field} checked={field.value} />}
                                        />
                                        <FormHelperText>
                                            Marque se deseja ignorar os feriados da semana.
                                        </FormHelperText>
                                    </FormControl>
                                )}
                            />
                        </Grid>
                    </CardContent>
                    <CardActions className={classes.cardActions}>
                        <Button
                            className={classes.button}
                            onClick={() => setOpen(false)}
                            disabled={mutation.isLoading}
                        >
                            Cancelar
                        </Button>
                        <Button
                            type="submit"
                            className={defaultClasses.addButton}
                            disabled={mutation.isLoading}
                        >
                            {mutation.isLoading ? "Salvando..." : "Salvar"}
                        </Button>
                    </CardActions>
                </Card>
            </Dialog>
        </>
    );
}
